

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewInsuranceModal = ({
    isOpen,
    onClose,
    onSave,
    travelOrderId = null
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        issue_date: '',
        expiry_date: '',
        travel_order_id: travelOrderId || '',
        premium: '',
        coverage_amount: '',
        age: '',
        gender: '',
        full_name: '',
        policy_number: '',
        provider: '',
        policy_type: '',
        coverage: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [travelOrders, setTravelOrders] = useState([]);
    const [loadingTravelOrders, setLoadingTravelOrders] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen && !travelOrderId) {
            fetchTravelOrders();
        }
    }, [isOpen, travelOrderId]);

    const fetchTravelOrders = async () => {
        setLoadingTravelOrders(true);
        try {
            const response = await axiosInstance.get('/travel-orders/');
            setTravelOrders(response.data || []);
        } catch (error) {
            console.error('Error fetching travel orders:', error);
        } finally {
            setLoadingTravelOrders(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Insurance Number is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                travel_order_id: formData.travel_order_id ? parseInt(formData.travel_order_id) : null,
                premium: formData.premium ? parseInt(formData.premium) : null,
                coverage_amount: formData.coverage_amount ? parseInt(formData.coverage_amount) : null,
                age: formData.age ? parseInt(formData.age) : null,
                issue_date: formData.issue_date || null,
                expiry_date: formData.expiry_date || null,
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            setFormData({
                name: '',
                issue_date: '',
                expiry_date: '',
                travel_order_id: travelOrderId || '',
                premium: '',
                coverage_amount: '',
                age: '',
                gender: '',
                full_name: '',
                policy_number: '',
                provider: '',
                policy_type: '',
                coverage: ''
            });
        } catch (error) {
            console.error('Error saving Insurance:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Insurance',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '90%', md: '800px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Insurance</MDTypography>
                        <IconButton onClick={onClose} variant="outlined" margin="normal" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Insurance Number"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                        required
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>

                                {!travelOrderId && (
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" margin="normal">
                                            <InputLabel>Travel Order</InputLabel>
                                            <Select
                                                name="travel_order_id"
                                                value={formData.travel_order_id}
                                                onChange={handleInputChange}
                                                label="Travel Order"

                                                disabled={loadingTravelOrders}
                                            >
                                                <MenuItem value=""><em>Select a Travel Order</em></MenuItem>
                                                {travelOrders.map(to => (
                                                    <MenuItem key={to.id} value={to.id}>{to.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Full Name"
                                        name="full_name"
                                        value={formData.full_name}
                                        onChange={handleInputChange}
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Policy Number"
                                        name="policy_number"
                                        value={formData.policy_number}
                                        onChange={handleInputChange}
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Provider"
                                        name="provider"
                                        value={formData.provider}
                                        onChange={handleInputChange}
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Policy Type"
                                        name="policy_type"
                                        value={formData.policy_type}
                                        onChange={handleInputChange}
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Coverage"
                                        name="coverage"
                                        multiline
                                        rows={3}
                                        value={formData.coverage}
                                        onChange={handleInputChange}
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Premium"
                                        name="premium"
                                        type="number"
                                        value={formData.premium}
                                        onChange={handleInputChange}
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Coverage Amount"
                                        name="coverage_amount"
                                        type="number"
                                        value={formData.coverage_amount}
                                        onChange={handleInputChange}
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Age"
                                        name="age"
                                        type="number"
                                        value={formData.age}
                                        onChange={handleInputChange}
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Gender</InputLabel>
                                        <Select
                                            name="gender"
                                            value={formData.gender}
                                            onChange={handleInputChange}
                                            label="Gender"

                                        >
                                            <MenuItem value=""><em>None</em></MenuItem>
                                            <MenuItem value="Male">Male</MenuItem>
                                            <MenuItem value="Female">Female</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Issue Date"
                                        name="issue_date"
                                        type="date"
                                        value={formData.issue_date}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Expiry Date"
                                        name="expiry_date"
                                        type="date"
                                        value={formData.expiry_date}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Insurance'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewInsuranceModal;

